/**
 * ehr#437 (FAMILY_COUPLES_NOTE_10-14-V15): what a family or couples note needs before sign-off,
 * beyond the progress note's own checks. The server checks the same (clinical-notes submit).
 */
import {
  partnerSafetyDone,
  partnerSafetyOpen,
  type FamilyAttendeeView,
  type FamilyType,
  type PartnerSafety,
} from '@ehr/shared';
import type { SignCheck } from './noteV81.js';

/**
 * 60 A: Safety between partners shows on a Couples session, or once this partner has answers (an
 * open Yes stays visible on Family). Not for someone who wasn't there.
 */
export function partnerSafetyShown(
  type: FamilyType,
  present: boolean,
  p: PartnerSafety | null,
): boolean {
  if (!present) return false;
  return type === 'Couples' || (!!p && !p.notPartner && (p.a ?? []).some(Boolean));
}

export function familySignChecks(n: {
  type: FamilyType;
  /** This attendee's attendance isn't Not present. */
  present: boolean;
  partnerSafety: PartnerSafety | null;
  /** The identified client's note. */
  billed: boolean;
  /** Everyone else on the session is Not present (70 A). */
  othersAway: boolean;
  firstName: string;
  /** ehr#571 round 2 (V11): the session has a Session summary (false asks for it). */
  summaryWritten?: boolean;
}): SignCheck[] {
  const out: SignCheck[] = [];
  if (n.billed && n.present && n.othersAway)
    out.push({ label: `Only ${n.firstName} came: use a Progress Note`, target: 'famOnly' });
  if (n.present && partnerSafetyOpen(n.partnerSafety))
    out.push({ label: 'Review safety between partners', target: 'pvs' });
  else if (n.type === 'Couples' && n.present && !partnerSafetyDone(n.partnerSafety))
    out.push({ label: 'Safety between partners', target: 'pvs' });
  // everyone who came, and the client's own note (90846 when they were Not present), as the server
  if ((n.present || n.billed) && n.summaryWritten === false)
    out.push({ label: 'Session summary', target: 'gsum' });
  return out;
}

/** Everyone on the session but the client is Not present (and there is someone else). */
export function othersAway(
  attendees: Pick<FamilyAttendeeView, 'billed' | 'onRoster' | 'attendance'>[],
) {
  const others = attendees.filter((a) => a.onRoster && !a.billed);
  return others.length > 0 && others.every((a) => a.attendance === 'Not present');
}

/** 60 A: who to ask alone before the session goes to Couples: there, note not signed, not asked. */
export function partnersToAsk(attendees: FamilyAttendeeView[]): FamilyAttendeeView[] {
  return attendees.filter(
    (a) =>
      a.onRoster &&
      a.attendance !== 'Not present' &&
      !(a.note && a.note.status !== 'draft' && a.note.status !== 'changes') &&
      (a.safety === 'none' || a.safety === 'partial'),
  );
}

/** ehr#689 (COUPLES_AND_FAMILY_NOTES_10-29-V15, V13 26 A): the family / couples note's name. */
export const FAMILY_NOTE_TITLE = 'Couples and Family Notes';
/** A note started as Family or Couples (body.noteType). */
export const isFamilyKind = (kind: string) => kind === 'Family' || kind === 'Couples';

/**
 * ehr#689 (V15, V11 fix 4): "+ Add attendee" is for the session's lead (or an admin / manager,
 * the page's `lead`), and only while some attendee's note is not yet signed or co-signed. A note
 * with the reviewer (Pending review) still counts as not signed.
 */
export function addAttendeeShown(
  lead: boolean,
  attendees: Pick<FamilyAttendeeView, 'onRoster' | 'note'>[],
): boolean {
  if (!lead) return false;
  const roster = attendees.filter((a) => a.onRoster);
  return !roster.every((a) => a.note?.status === 'signed' || a.note?.status === 'cosigned');
}
